import {
  Button,
  Card,
  ColorInput,
  Drawer,
  FileInput,
  Group,
  Image,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { type BulkRow, PRIZE_TYPES } from "./types.ts";

type Props = {
  opened: boolean;
  onClose: () => void;
  bulkRows: BulkRow[];
  bulkSaving: boolean;
  pasteOpen: boolean;
  pasteText: string;
  onSetPasteOpen: (v: boolean) => void;
  onSetPasteText: (v: string) => void;
  onUpdateBulkRow: (idx: number, patch: Partial<BulkRow>) => void;
  onAddBulkRow: () => void;
  onCopyLastBulkRow: () => void;
  onRemoveBulkRow: (idx: number) => void;
  onBulkSave: () => void;
  onBulkImageUpload: (idx: number, file: File | null) => void;
  onSetBulkRows: (rows: BulkRow[]) => void;
  existingCodes: ReadonlySet<string>;
};

export function BulkCreateDrawer({
  opened,
  onClose,
  bulkRows,
  bulkSaving,
  pasteOpen,
  pasteText,
  onSetPasteOpen,
  onSetPasteText,
  onUpdateBulkRow,
  onAddBulkRow,
  onCopyLastBulkRow,
  onRemoveBulkRow,
  onBulkSave,
  onBulkImageUpload,
  onSetBulkRows,
  existingCodes,
}: Props) {
  const getRowCodeError = (code: string, idx: number): string | null => {
    const trimmed = code.trim();
    if (trimmed.length === 0) return "请填写 code";
    if (existingCodes.has(trimmed)) return "该 code 已被现有奖品占用（含已禁用），请换一个";
    const isDuplicate = bulkRows.some(
      (r, i) => i !== idx && r.code.trim() === trimmed && trimmed.length > 0,
    );
    if (isDuplicate) return "与本批其他行 code 重复";
    return null;
  };

  const handleSaveWithValidation = (): void => {
    const errors = bulkRows.map((row, idx) => getRowCodeError(row.code, idx));
    const hasError = errors.some((e) => e !== null);
    if (hasError) {
      const updated = bulkRows.map((row, idx) => {
        const codeErr = errors[idx];
        if (codeErr !== null) return { ...row, _error: `code：${codeErr}` };
        return row;
      });
      onSetBulkRows(updated);
      notifications.show({ color: "orange", message: "存在 code 错误，请修正后再保存" });
      return;
    }
    onBulkSave();
  };

  const handlePasteImport = (): void => {
    try {
      const parsed = JSON.parse(pasteText) as unknown[];
      if (!Array.isArray(parsed)) throw new Error("not array");
      const asStr = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
      const asNum = (v: unknown, d = 0): number => (typeof v === "number" ? v : d);
      const asLimitStr = (v: unknown): string =>
        typeof v === "number" || typeof v === "string" ? String(v) : "";
      const rows: BulkRow[] = parsed.map((item) => {
        const obj = (item ?? {}) as Record<string, unknown>;
        return {
          code: asStr(obj["code"]),
          name: asStr(obj["name"]),
          type: PRIZE_TYPES.includes(asStr(obj["type"])) ? asStr(obj["type"]) : "thanks",
          weight: asNum(obj["weight"], 1),
          valueAmount: asNum(obj["valueAmount"], 0),
          stock: asLimitStr(obj["stock"]),
          dailyLimit: asLimitStr(obj["dailyLimit"]),
          imageUrl: asStr(obj["imageUrl"]),
          displayColor: asStr(obj["displayColor"]),
          requiresShipping: obj["requiresShipping"] === true,
        };
      });
      onSetBulkRows(rows);
      onSetPasteOpen(false);
      onSetPasteText("");
    } catch {
      notifications.show({ color: "red", message: "JSON 解析失败，请检查格式" });
    }
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title="批量创建奖品"
        position="right"
        size="xl"
        keepMounted={false}
      >
        <Stack gap="sm" style={{ height: "100%" }}>
          <Group gap="xs">
            <Button size="xs" variant="outline" onClick={onAddBulkRow}>
              + 新增一行
            </Button>
            <Button size="xs" variant="outline" onClick={onCopyLastBulkRow}>
              复制上一行
            </Button>
            <Button size="xs" variant="light" onClick={() => onSetPasteOpen(true)}>
              粘贴 JSON
            </Button>
          </Group>
          <ScrollArea style={{ flex: 1 }}>
            <Stack gap="md">
              {bulkRows.map((row, idx) => (
                <Card
                  key={idx}
                  withBorder
                  padding="sm"
                  radius="sm"
                  style={
                    row._error !== undefined
                      ? { borderColor: "var(--mantine-color-red-5)" }
                      : undefined
                  }
                >
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        行 {idx + 1}
                      </Text>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => onRemoveBulkRow(idx)}
                      >
                        删除
                      </Button>
                    </Group>
                    {row._error !== undefined ? (
                      <Text size="xs" c="red">
                        {row._error}
                      </Text>
                    ) : null}
                    <Group gap="xs" grow>
                      <TextInput
                        label="code"
                        size="xs"
                        value={row.code}
                        error={getRowCodeError(row.code, idx) ?? undefined}
                        onChange={(e) => onUpdateBulkRow(idx, { code: e.currentTarget.value })}
                      />
                      <TextInput
                        label="名称"
                        size="xs"
                        value={row.name}
                        onChange={(e) => onUpdateBulkRow(idx, { name: e.currentTarget.value })}
                      />
                    </Group>
                    <Group gap="xs" grow>
                      <Select
                        label="类型"
                        size="xs"
                        data={PRIZE_TYPES}
                        value={row.type}
                        onChange={(v) => onUpdateBulkRow(idx, { type: v ?? "thanks" })}
                      />
                      <NumberInput
                        label="权重"
                        size="xs"
                        min={0}
                        value={row.weight}
                        onChange={(v) => onUpdateBulkRow(idx, { weight: Number(v) })}
                      />
                      <NumberInput
                        label="价值"
                        size="xs"
                        min={0}
                        value={row.valueAmount}
                        onChange={(v) => onUpdateBulkRow(idx, { valueAmount: Number(v) })}
                      />
                    </Group>
                    <Group gap="xs" grow>
                      <TextInput
                        label="库存（空=无限）"
                        size="xs"
                        value={row.stock}
                        onChange={(e) => onUpdateBulkRow(idx, { stock: e.currentTarget.value })}
                      />
                      <TextInput
                        label="日上限（空=无限）"
                        size="xs"
                        value={row.dailyLimit}
                        onChange={(e) =>
                          onUpdateBulkRow(idx, { dailyLimit: e.currentTarget.value })
                        }
                      />
                    </Group>
                    <Group gap="xs" grow>
                      <ColorInput
                        label="展示颜色"
                        size="xs"
                        value={row.displayColor}
                        onChange={(v) => onUpdateBulkRow(idx, { displayColor: v })}
                      />
                      <FileInput
                        label="图片"
                        size="xs"
                        accept="image/*"
                        placeholder="选择图片"
                        disabled={row._uploading === true}
                        onChange={(file) => onBulkImageUpload(idx, file)}
                      />
                    </Group>
                    {row.imageUrl !== "" ? <Image src={row.imageUrl} h={40} fit="contain" /> : null}
                    {row.type === "physical" ? (
                      <Switch
                        label="实物需要收件"
                        size="xs"
                        checked={row.requiresShipping}
                        onChange={(e) =>
                          onUpdateBulkRow(idx, { requiresShipping: e.currentTarget.checked })
                        }
                      />
                    ) : null}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </ScrollArea>
          <Button
            loading={bulkSaving}
            disabled={bulkRows.length === 0}
            onClick={handleSaveWithValidation}
          >
            全部保存（{bulkRows.length} 条）
          </Button>
        </Stack>
      </Drawer>

      {/* 粘贴 JSON Modal */}
      <Modal
        title="粘贴 JSON 导入"
        opened={pasteOpen}
        onClose={() => onSetPasteOpen(false)}
        keepMounted={false}
      >
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            粘贴 JSON 数组，字段：code / name / type / weight / valueAmount / stock / dailyLimit /
            imageUrl / displayColor / requiresShipping
          </Text>
          <Textarea
            minRows={8}
            autosize
            value={pasteText}
            onChange={(e) => onSetPasteText(e.currentTarget.value)}
            placeholder='[{"code":"prize1","name":"奖品一","type":"thanks","weight":10}]'
          />
          <Button onClick={handlePasteImport}>导入到行编辑</Button>
        </Stack>
      </Modal>
    </>
  );
}
